import type { ConfigAuditEntry } from '@servicetim/shared';
import { useAuditLog } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  pagedTableProps,
} from '@servicetim/ui/components';
import { Eye } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatDateTime, humanize, shortId } from '../common/format';
import { diffJson } from './audit-diff';
import { AuditActionBadge, AuditDiffDrawer } from './audit-diff-drawer';
import { type AuditFilterState, AuditFilters } from './audit-filters';

const changeCount = (entry: ConfigAuditEntry): number =>
  diffJson(entry.before, entry.after).filter((e) => e.kind !== 'unchanged').length;

/** Newest-first configuration audit trail with server filters and a client-side action filter. */
export function AuditTable() {
  const [filters, setFilters] = useState<AuditFilterState>({});
  const { action, ...serverFilters } = filters;
  const log = useAuditLog(serverFilters);
  const [selected, setSelected] = useState<ConfigAuditEntry | null>(null);

  const rows = useMemo(
    () => (action ? log.items.filter((e) => e.action === action) : log.items),
    [log.items, action],
  );

  const columns = useMemo<ColumnDef<ConfigAuditEntry, unknown>[]>(
    () => [
      {
        accessorKey: 'occurredAt',
        header: 'When',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-fg-muted">
            {formatDateTime(row.original.occurredAt)}
          </span>
        ),
      },
      {
        id: 'actor',
        header: 'Actor',
        accessorFn: (entry) => entry.actor?.displayName ?? '',
        cell: ({ row }) =>
          row.original.actor ? (
            <span className="font-medium text-fg">{row.original.actor.displayName}</span>
          ) : (
            <span className="text-fg-muted italic">System</span>
          ),
      },
      {
        accessorKey: 'entityType',
        header: 'Entity',
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <Badge variant="secondary">{humanize(row.original.entityType)}</Badge>
            <code className="font-mono text-fg-muted text-xs" title={row.original.entityId}>
              {shortId(row.original.entityId)}
            </code>
          </span>
        ),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: ({ row }) => <AuditActionBadge action={row.original.action} />,
      },
      {
        id: 'changes',
        header: 'Changes',
        enableSorting: false,
        cell: ({ row }) => {
          const n = changeCount(row.original);
          return <span className="text-fg-muted">{n === 0 ? '—' : n}</span>;
        },
      },
      {
        id: 'view',
        header: () => <span className="sr-only">Details</span>,
        enableSorting: false,
        size: 48,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="View changes"
            onClick={(event) => {
              event.stopPropagation();
              setSelected(row.original);
            }}
          >
            <Eye />
          </Button>
        ),
      },
    ],
    [],
  );

  const paged = pagedTableProps(log);

  return (
    <>
      <DataTable
        columns={columns}
        {...paged}
        data={paged.data ? rows : undefined}
        getRowId={(e) => e.id}
        caption="Configuration audit log"
        dense
        onRowClick={setSelected}
        toolbar={<AuditFilters value={filters} onChange={setFilters} />}
        footer={
          paged.data
            ? `${rows.length} of ${log.items.length} loaded${log.hasMore ? ' (more available)' : ''}${action ? ` · action filter applies to loaded pages` : ''}`
            : null
        }
        emptyTitle="No audit entries"
        emptyDescription="Configuration changes appear here as they happen. Try widening the filters."
      />
      <AuditDiffDrawer entry={selected} onClose={() => setSelected(null)} />
    </>
  );
}
